// src/components/ImagePrompts.jsx
import { useEffect, useState, useRef } from "react";
import { generateImage } from "../utils/api";
import { useAuth } from "../auth/AuthProvider";
import { useLocation, Navigate } from "react-router-dom";
import { safeCapture } from "../utils/analytics";
import { jsonAuthHeaders } from "../utils/authHeaders";

// Platform visual characteristics (NOT mentioned by name in prompt)
const platformStyles = {
  instagram_square: {
    composition: "Centered subject, balanced framing, square composition",
    vibe: "Lifestyle aesthetic, bright and engaging, social media ready",
  },
  instagram_portrait: {
    composition: "Vertical composition, subject in upper two-thirds, room for text at bottom",
    vibe: "Eye-catching, scroll-stopping, mobile-optimized",
  },
  instagram_landscape: {
    composition: "Wide horizontal layout, panoramic feel",
    vibe: "Expansive, cinematic, landscape orientation",
  },
  instagram_story: {
    composition: "Tall vertical format, full-screen mobile, subject centered vertically",
    vibe: "Bold, immersive, story-style vertical",
  },
  reel_cover: {
    composition: "Tall vertical, action-oriented framing, dynamic",
    vibe: "Eye-catching thumbnail, energetic, motion-suggesting",
  },
  pinterest: {
    composition: "Tall vertical pin format, text-friendly top/bottom space",
    vibe: "Aspirational, Pinterest aesthetic, inspiration-focused",
  },
  tiktok_cover: {
    composition: "Vertical mobile-first, bold central subject",
    vibe: "Attention-grabbing, Gen-Z aesthetic, trend-aware",
  },
  hero_banner: {
    composition: "Wide horizontal banner, subject on left or right third, negative space for text",
    vibe: "Professional, web-ready, hero section aesthetic",
  },
  wide_hero: {
    composition: "Ultra-wide panoramic banner, cinematic framing, expansive negative space",
    vibe: "Epic, immersive, premium web experience",
  },
  blog_featured: {
    composition: "Landscape format, article header style, clean and readable",
    vibe: "Editorial quality, blog-appropriate, trustworthy",
  },
  inline_illustration: {
    composition: "Balanced 4:3 format, article-inline friendly",
    vibe: "Clear, informative, content-supporting",
  },
  mascot: {
    composition: "Character-focused, full body or portrait, white or transparent-ready background",
    vibe: "Memorable, brand personality, mascot character design",
  },
  brand_texture: {
    composition: "Abstract pattern or texture, seamless-tile friendly",
    vibe: "Brand aesthetic, background-ready, versatile usage",
  },
  brand_scene: {
    composition: "Environmental scene showcasing brand aesthetic",
    vibe: "Brand storytelling, atmosphere-driven, identity-focused",
  },
  social_template: {
    composition: "Template layout with clear content zones, text-placement friendly",
    vibe: "Versatile, reusable, template aesthetic",
  },
  promo_template: {
    composition: "Promotional layout, vertical portrait, attention-grabbing zones",
    vibe: "Sales-ready, conversion-focused, promotional design",
  },
  quote_template: {
    composition: "Quote-friendly layout, central text space, decorative framing",
    vibe: "Inspirational, shareable, quote-card aesthetic",
  },
  carousel_template: {
    composition: "Multi-slide carousel style, swipeable format suggestion",
    vibe: "Educational, story-driven, carousel-ready",
  },
};

// Map subtype to platform style key
const getPlatformStyle = (subtype) => {
  const mapping = {
    "Instagram 1:1 Square": "instagram_square",
    "Instagram 4:5 Portrait": "instagram_portrait",
    "Instagram 1.91:1 Landscape": "instagram_landscape",
    "Instagram Story 9:16": "instagram_story",
    "Reel Cover 9:16": "reel_cover",
    "Pinterest 2:3": "pinterest",
    "TikTok Cover 9:16": "tiktok_cover",
    "Hero Banner 16:9": "hero_banner",
    "Wide Hero 21:9": "wide_hero",
    "Blog Featured Image 16:9": "blog_featured",
    "Inline Illustration 4:3": "inline_illustration",
    "Mascot / Character": "mascot",
    "Brand Texture": "brand_texture",
    "Brand Scene": "brand_scene",
    "Social Template 1:1": "social_template",
    "Promo Template 4:5": "promo_template",
    "Quote Template 1:1": "quote_template",
    "Carousel Template 4:5": "carousel_template",
  };

  const key = mapping[subtype];
  return key ? platformStyles[key] : null;
};

// Canon T7 Rebel visual characteristics (NOT the camera itself)
const getCameraStyleCharacteristics = () => {
  return {
    lighting: "Natural balanced lighting with good dynamic range",
    depth: "Shallow depth of field with beautiful background blur (bokeh effect)",
    sharpness: "Sharp focus on subject with professional detail",
    color: "Rich, accurate color reproduction with slight warmth",
    quality: "DSLR quality, professional photography aesthetic",
    overall: "Entry-level DSLR look: natural, well-exposed, slight background separation",
  };
};

// Camera profiles (UI + prompt add-ons)
const cameraProfiles = {
  none: {
    title: "Default look",
    description: "No camera preset applied. Uses clean, neutral, platform-ready rendering.",
    promptParts: [],
  },
  canon_t7: {
    title: "Warm DSLR look (Canon T7 style)",
    description:
      "Adds DSLR photography characteristics: natural lighting, shallow depth of field with bokeh, sharp subject focus, rich warm colors, professional entry-level DSLR aesthetic.",
    promptParts: (() => {
      const c = getCameraStyleCharacteristics();
      return [c.lighting, c.depth, c.sharpness, c.color, c.overall];
    })(),
  },
  sony_a9iii: {
    title: "Crisp action freeze (Sony A9 III style)",
    description:
      "Action-freeze look: ultra-sharp motion capture, wide-angle perspective, crisp flash-lit subject edges, frozen treat toss timing, dramatic near-to-far depth.",
    promptParts: [
      "Ultra-fast action freeze with zero motion blur",
      "Crisp flash-lit subject with clean edges and controlled light falloff",
      "Wide-angle lens look (12–24mm feel), low perspective, dramatic near-to-far depth",
      "Sharp eyes and sharp face detail, high micro-contrast",
      "Treat frozen mid-air at peak moment; tongue slightly forward to meet the treat; open mouth catches if tongue misses",
    ],
  },
};

export default function ImagePrompts() {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return <div style={{ padding: 24, opacity: 0.8 }}>Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const userEmail = user.email || "";
  const userId = user.id; // reserved for future bucket keys (preferred)

  // -----------------------------
  // FORM STATE (NO EMAIL)
  // -----------------------------
  const [formData, setFormData] = useState({
    category: "social",
    subtype: "",
    subject: "",
    style: "",
    mood: "",
    colors: "",
    overlay: "",
    aspect: "",
    quality: "SD",
    cameraProfile: "none",
  });

  // -----------------------------
  // IMAGE GENERATION STATE
  // -----------------------------
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [referenceFile, setReferenceFile] = useState(null);
  const fileInputRef = useRef(null);

  // =============================
  // USAGE GATING (Supabase buckets)
  // =============================
  const [usageStatus, setUsageStatus] = useState({
    loading: false,
    loaded: false,
    isPro: false,
    balance: 0,

    freeSdLeft: 0,
    freeHdLeft: 0,
    freeResetsAt: null,

    proCreditsLeft: 0,
    proResetsAt: null,

    error: null,
  });

  function normalizeStatus(raw) {
    const isPro = !!raw?.isPro;
    const balance = Number(raw?.balance ?? 0);

    const freeImagesLeft = Number(raw?.free?.images_weekly_left ?? 0);
    const freeResetsAt = raw?.free?.resets_at ?? null;

    const proCreditsLeft = Number(raw?.pro?.monthly_credits_left ?? balance ?? 0);
    const proResetsAt = raw?.pro?.resets_at ?? null;

    return {
      loading: false,
      loaded: true,
      isPro,
      balance,

      freeSdLeft: freeImagesLeft,
      freeHdLeft: freeImagesLeft,
      freeResetsAt,

      proCreditsLeft,
      proResetsAt,

      error: null,
    };
  }

  async function refreshUsageStatus(email) {
    const e = (email || "").trim();
    if (!e || !e.includes("@")) {
      setUsageStatus((s) => ({
        ...s,
        loading: false,
        loaded: false,
        error: "Signed in, but email missing.",
      }));
      return;
    }

    setUsageStatus((s) => ({ ...s, loading: true, error: null }));

    try {
      const API_BASE = import.meta.env.PROD ? "https://www.bluedobiedev.com" : "";

      const res = await fetch(`${API_BASE}/api/credits/status`, {
        method: "POST",
        headers: await jsonAuthHeaders(),
        body: JSON.stringify({ email: e }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `status_${res.status}`);
      }

      setUsageStatus(normalizeStatus(data));
    } catch (err) {
      console.error("Failed to refresh usage status:", err);
      setUsageStatus((s) => ({
        ...s,
        loading: false,
        loaded: true,
        error: "Could not load usage status.",
      }));
    }
  }

  // Refresh on mount / when authenticated email changes
  useEffect(() => {
    refreshUsageStatus(userEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const selectedQuality = (formData.quality || "SD").toUpperCase() === "HD" ? "HD" : "SD";
  const selectedCost = selectedQuality === "HD" ? 2 : 1;

  function canGenerateImageNow() {
    const email = userEmail;
    if (!email || !email.includes("@")) return false;
    if (usageStatus.loading) return false;
    if (usageStatus.error) return false;

    if (usageStatus.isPro) {
      return (usageStatus.proCreditsLeft ?? 0) >= selectedCost;
    }

    return selectedQuality === "HD"
      ? (usageStatus.freeHdLeft ?? 0) >= 1
      : (usageStatus.freeSdLeft ?? 0) >= 1;
  }

  function getUsageLabel() {
    const email = userEmail;
    if (!email || !email.includes("@")) return "Signed in, but email missing.";
    if (usageStatus.loading && !usageStatus.loaded) return "Checking limits…";
    if (usageStatus.error) return usageStatus.error;

    if (usageStatus.isPro) {
      const left = usageStatus.proCreditsLeft ?? usageStatus.balance ?? 0;
      return `Pro: ${left} credits left this month (SD=1, HD=2)`;
    }

    return `Free weekly: ${usageStatus.freeSdLeft} SD left or ${usageStatus.freeHdLeft} HD left`;
  }

  async function afterImageGeneratedRefresh() {
    await refreshUsageStatus(userEmail);
  }

  // -----------------------------
  // SMART PROMPT BUILDER
  // -----------------------------
  const buildPrompt = () => {
    const parts = [];

    const platformStyle = getPlatformStyle(formData.subtype);

    if (formData.subject) parts.push(formData.subject);

    if (platformStyle) {
      parts.push(platformStyle.composition);
      parts.push(platformStyle.vibe);
    }

    const selectedCamera = cameraProfiles[formData.cameraProfile] || cameraProfiles.none;
    if (selectedCamera.promptParts?.length) {
      parts.push(...selectedCamera.promptParts);
    }


    if (formData.style) parts.push(`Style: ${formData.style}`);
    if (formData.mood) parts.push(`Mood: ${formData.mood}`);
    if (formData.colors) parts.push(`Brand colors: ${formData.colors}`);
    if (formData.overlay) parts.push(`Include text overlay: "${formData.overlay}"`);

    parts.push("Professional photography quality, clean composition, platform-ready");

    return parts.filter(Boolean).join(". ") + ".";
  };

  const finalPrompt = buildPrompt();

  // -----------------------------
  // SUBTYPE OPTIONS
  // -----------------------------
  const subtypeOptions = {
    social: [
      "Instagram 1:1 Square",
      "Instagram 4:5 Portrait",
      "Instagram 1.91:1 Landscape",
      "Instagram Story 9:16",
      "Reel Cover 9:16",
      "Pinterest 2:3",
      "TikTok Cover 9:16",
    ],
    web: ["Hero Banner 16:9", "Wide Hero 21:9", "Blog Featured Image 16:9", "Inline Illustration 4:3"],
    branding: ["Mascot / Character", "Brand Texture", "Brand Scene"],
    template: ["Social Template 1:1", "Promo Template 4:5", "Quote Template 1:1", "Carousel Template 4:5"],
  };

  const aspectFromSubtype = (sub) => {
    if (!sub) return "";
    if (sub.includes("1:1")) return "1:1";
    if (sub.includes("4:5")) return "4:5";
    if (sub.includes("9:16")) return "9:16";
    if (sub.includes("1.91")) return "1.91:1";
    if (sub.includes("2:3")) return "2:3";
    if (sub.includes("16:9")) return "16:9";
    if (sub.includes("21:9")) return "21:9";
    if (sub.includes("4:3")) return "4:3";
    return "";
  };

  const handleSubtypeChange = (value) => {
    const asp = aspectFromSubtype(value);
    setFormData((f) => ({ ...f, subtype: value, aspect: asp }));
  };

  // -----------------------------
  // IMAGE GENERATION
  // -----------------------------
  const handleGenerate = async () => {
    setError(null);

    const email = userEmail;
    if (!email || !email.includes("@")) {
      setError("Signed in, but email missing.");
      return;
    }

    if (!finalPrompt || finalPrompt.length < 10) {
      setError("Please enter enough details for the image.");
      return;
    }

    if (!canGenerateImageNow()) {
      setError(
        usageStatus.isPro
          ? `Not enough credits for ${selectedQuality}. (SD=1, HD=2)`
          : `Free weekly limit reached for ${selectedQuality}.`
      );
      return;
    }

    const startedAt = Date.now();

    try {
      setLoading(true);

      const imageUrl = await generateImage(finalPrompt, email, formData.aspect, referenceFile, selectedQuality);

      if (!imageUrl) throw new Error("Invalid API response");

      setGeneratedImage(imageUrl);

      await afterImageGeneratedRefresh();

      safeCapture("image_generated", {
        route: window.location.pathname,
        is_pro: !!usageStatus.isPro,
        quality: selectedQuality,
        aspect: formData.aspect,
        ms: Date.now() - startedAt,
      });
    } catch (err) {
  console.error(err);

  const message =
    err?.message || "Something went wrong generating the image.";

  setError(message);

  safeCapture("image_generation_failed", {
    route: window.location.pathname,
    is_pro: !!usageStatus.isPro,
    quality: selectedQuality,
    message: String(message).slice(0, 120),
    ms: Date.now() - startedAt,
  });
} finally {
      setLoading(false);
    }
  };

  const buttonDisabled = loading || !canGenerateImageNow();

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">DobieCore Images</h1>
      <p className="text-gray-600 mb-8">
        Generate platform-ready AI images with guided prompts and Sabre&apos;s Big Green Button.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT: FORM */}
        <div className="space-y-6">
          {/* Usage Status (no email input) */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">{getUsageLabel()}</p>
          </div>

          {/* Quality */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Quality</label>
            <select
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.quality}
              onChange={(e) => setFormData({ ...formData, quality: e.target.value })}
            >
              <option value="SD">SD (1 credit)</option>
              <option value="HD">HD (2 credits)</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.category}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category: e.target.value,
                  subtype: "",
                  aspect: "",
                })
              }
            >
              <option value="social">Social</option>
              <option value="web">Web</option>
              <option value="branding">Branding</option>
              <option value="template">Template</option>
            </select>
          </div>

          {/* Subtype */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Subtype</label>
            <select
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.subtype}
              onChange={(e) => handleSubtypeChange(e.target.value)}
            >
              <option value="">Select subtype…</option>
              {subtypeOptions[formData.category].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Subject / Main Idea</label>
            <textarea
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm h-24"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="cropped and docked Blue Doberman sitting beside a laptop that shows code work"
            />
          </div>

          {/* Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Style</label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.style}
              onChange={(e) => setFormData({ ...formData, style: e.target.value })}
              placeholder="photorealistic, flat illustration, watercolor…"
            />
          </div>

          {/* Camera Style */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <select
                value={formData.cameraProfile}
                onChange={(e) => setFormData({ ...formData, cameraProfile: e.target.value })}
                className="h-10 px-3 border border-gray-300 rounded-md text-gray-900 bg-white shadow-sm"
              >
                <option value="none">Default look</option>
                <option value="canon_t7">Warm DSLR look (Canon T7 style)</option>
                <option value="sony_a9iii">Crisp action freeze (Sony A9 III style)</option>
              </select>

              {(() => {
                const selected = cameraProfiles[formData.cameraProfile] || cameraProfiles.none;
                return (
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{selected.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{selected.description}</p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Mood */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Mood / Vibe</label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.mood}
              onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
              placeholder="playful, cozy, bold, professional…"
            />
          </div>

          {/* Colors */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Brand Colors</label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.colors}
              onChange={(e) => setFormData({ ...formData, colors: e.target.value })}
              placeholder="navy blue, orange, white…"
              title="Add hex codes, RGB values, or simple color names. DobieCore uses them as gentle visual direction, not literal paint-by-number instructions."
            />
            <p className="mt-1 text-xs text-slate-600">
              Use your brand colors to influence the mood of your image. Colors are applied subtly for a cohesive look, not to overpower the image.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Add hex codes, RGB values, or simple color names. DobieCore uses them as gentle visual direction, not literal paint-by-number instructions.
            </p>
          </div>

          {/* Text overlay */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Text Overlay (optional)</label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm"
              value={formData.overlay}
              onChange={(e) => setFormData({ ...formData, overlay: e.target.value })}
            />
          </div>

  {/* Reference Image Upload */}
  <div className="mb-4">
    <label className="block mb-1 font-medium">Reference Image (optional)</label>

    <input
      ref={fileInputRef}
      type="file"
      accept="image/png,image/jpeg,image/webp"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) {
          setError("Reference image must be under 50MB.");
          return;
        }

        setReferenceFile(file);
        setError(null);
      }}
      className="block w-full text-sm text-slate-700 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-slate-300 file:text-sm file:bg-slate-100 file:cursor-pointer hover:file:bg-slate-200"
    />

    {/* Selected file display */}
    {referenceFile && (
      <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm">
        <span className="text-green-800">
          Selected: <strong>{referenceFile.name}</strong>
        </span>

        <button
          type="button"
          onClick={() => {
            setReferenceFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          className="text-red-600 hover:text-red-800 font-medium"
        >
          Clear
        </button>
      </div>
    )}

    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
      <h3 className="font-semibold mb-1">Reference Image Safe Limits</h3>
      <ul className="list-disc list-inside space-y-1">
        <li>PNG, JPG/JPEG, or WebP files only.</li>
        <li>Max file size: <span className="font-medium">50 MB</span>.</li>
        <li>At least <span className="font-medium">512px</span> on shortest side.</li>
        <li>Clear, well-lit photo recommended.</li>
      </ul>
      <p className="mt-2 text-xs text-slate-500">
        DobieCore uses your reference image to maintain subject consistency while reimagining the scene.
      </p>
    </div>
  </div>
</div>

{/* RIGHT: PREVIEW + BUTTON */}
<div className="space-y-6">
          {/* Prompt Preview */}
          <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-2">Prompt Preview</h3>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{finalPrompt}</pre>
          </div>

          {/* Sabre's Big Green Button */}
          <button
            onClick={handleGenerate}
            disabled={buttonDisabled}
            className={`w-full py-4 rounded-lg shadow-md text-white font-semibold text-lg transition-all ${
              buttonDisabled ? "bg-green-300 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 hover:scale-[1.02]"
            }`}
          >
            {loading ? "Generating…" : "Create Image"}
          </button>

          {/* Error */}
          {error && <div className="text-red-600 text-sm font-medium">{error}</div>}

          {/* Output Image */}
          {generatedImage && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-2">Generated Image</h3>
              <img src={generatedImage} alt="AI output" className="rounded-lg shadow-md border border-gray-200" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
